import dayJS, { dateFormat } from "@/app/utils/DayJS";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { textSize } from "@/app/utils/MixinSize";
import { Link } from "@mui/material";
import Box from "@mui/material/Box";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import dayjs from "dayjs";
import React, { useEffect } from 'react';
import styled from "styled-components";
import FixtureFilter from '../fixtureFilter';
import { useMyContext } from "@/app/utils/MyContext";

const Container = styled.div`
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  overflow: hidden; 
`;

const IconNote = styled.div`
  width: 100%; 
  display: flex;
  margin-Top: 2.5vw; 
  padding: 0 4px;
`;

const MessageDiv = styled.div`
  width: 100%; 
  padding: 15px;
  text-align: center;
`;

const StyledTableCell = styled(TableCell) <{ ismobile?: string }>`
    ${({ ismobile }) => ({
        [`&.${tableCellClasses.root}`]: {
            color: "#333333",
        },
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: "#E1E1E1",
            fontWeight: 400,
            ...textSize(11),
            padding: ismobile == "true" ? "8px 4px 8px 8px" : "8px 8px 8px 12px"
        },
        [`&.${tableCellClasses.body}`]: {
            ...textSize(13),
            fontWeight: 500,
            padding: ismobile == "true" ? "8px 0px 8px 8px" : "8px 8px 8px 12px",
            verticalAlign: "baseline",
            lineHeight: "17px"
        },
        [`&.${tableCellClasses.body}:first-child`]: {
            // paddingLeft: ismobile =="true"  ? "2.13vw" : "12px"
        },
        [`&.${tableCellClasses.body}.tabCircleSty`]: {
            paddingRight: "1.06vw",
            paddingLeft: "0"
        },
        [`&.${tableCellClasses.body} i.isDisplay`]: {
            margin: ismobile == "true" ? "0 0 0 8px" : "0 auto"
        }
    })} 
`;

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(even)': {
        backgroundColor: "#F4F4F4",
    },
    '&:nth-of-type(odd)': {
        backgroundColor: "#FFFFFF",
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '& td, & th': {
        border: 0,
    }
}));

interface propType {
    isMobile: boolean;
    fixtureConfig:any;
    fixtureList:IFixture[];
}

const fixtureRaceSeries = ["WorldPool", "SimulcastDay", "SimulcastRace"];

const FixtureList: React.FC<propType> = (props: propType) => {
    const isMobile = props.isMobile ?? false;
    const { state: myContextState } = useMyContext();
    const { state: lang, dispatch: langDispatch } = useLanguage();
    const { t } = lang;
    const {fixtureConfig,fixtureList}=props;

    const [tabData, setTabData] = React.useState<IFixture[]>([]);
    const serverDateFormat = lang.langType === LANGTYPE_EN ? "M/D/YYYY" : "D/M/YYYY";

    const cutoffDate = window.globalConfig?.OTHERS?.CUTOFF_DATE;

    useEffect(() => {
        setTabData(props.fixtureList)
      }, [props.fixtureList]);
      
    function getDisplayDate(serverDate?: string) {
        return dayJS(serverDate, serverDateFormat).isValid() ? dayJS(serverDate, serverDateFormat).format(dateFormat.display.date) : "-"
    }

    const getRaceSeriesValue = (colNumber: number, row: any) => {
        const raceSeries = row?.raceSeries?.targetItems?.map((x: any) => x?.displayName);
        return raceSeries?.includes(fixtureRaceSeries[colNumber]);
    }

    const commFilter = (display: string, location: string) => {
        let filterData = [] as IFixture[]
        switch (display) {
            case "default":
                filterData = fixtureList;
                break;
            case "allCarnival":
                filterData = fixtureList.filter(x => x?.carnivalName?.value && x?.carnivalName?.value != "");
                break;
            default:
                filterData = fixtureList.filter(x => x?.carnivalName?.value == display);
        }

        if (location != "default") {
            filterData = filterData.filter(x => (isMobile ? x?.locationCode?.value : x?.locationName?.value) == location);
        }

        setTabData(filterData);
    }

    const getRaceNameCell = (row: IFixture) => {
        if (dayjs(row?.date_HK?.value, serverDateFormat).isBefore(dayjs(cutoffDate)) && row?.teamsiteURL?.value?.length) {
            return <Link href={row.teamsiteURL.value} target="_blank">{row?.raceName?.value?.toString()}</Link>
        }
        return row?.meetingRaceMapping?.targetItem ? (
            <Link href={row.redirectLink} target="_blank">{row?.raceName?.value?.toString()}</Link>) : row.raceName?.value?.toString()
    }

    return (
        <Container>
            <FixtureFilter data={fixtureList} onChange={commFilter} isMobile={isMobile}></FixtureFilter>
            <TableContainer className="">
                {tabData.length ?
                    <Table aria-label="simple table">
                        <colgroup>
                            <col width="8%" />
                            <col width="50%" />
                            <col width="12%" />
                            <col width="10%" />
                            <col width="10%" />
                            <col width="10%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell
                                    ismobile={isMobile.toString()}
                                    style={{ width: isMobile ? "21.8vw" : "78px", lineHeight: isMobile ? "4.26vw" : '' }}>
                                    {fixtureConfig?.date_HK?.value?.toString()}
                                </StyledTableCell>
                                <StyledTableCell ismobile={isMobile.toString()}>
                                    {isMobile ? (
                                        <div style={{ lineHeight: "4.26vw" }}>
                                            {fixtureConfig?.raceName?.value?.toString()}
                                        </div>
                                    ) : fixtureConfig?.raceName?.value?.toString()}
                                </StyledTableCell>
                                <StyledTableCell ismobile={isMobile.toString()} align="left" style={{ width: isMobile ? "" : "225px" }}>{fixtureConfig?.location?.value?.toString()}</StyledTableCell>
                                <StyledTableCell ismobile={isMobile.toString()} className={(isMobile ? "tabIconMobileSty " : '') + "tabIconSty"} style={{ width: isMobile ? "7.6vw" : "114px", color: "#173E96" }}>
                                    <Box><img src={`${myContextState.assetPrefix}/fixture/worldPool.svg`} /> {isMobile ? '' : fixtureConfig?.raceSeries1?.value?.toString()}</Box>
                                </StyledTableCell>
                                <StyledTableCell ismobile={isMobile.toString()} className={(isMobile ? "tabIconMobileSty " : '') + "tabIconSty"} style={{ width: isMobile ? "7.6vw" : "122px", color: "#173E96" }}>
                                    <Box><img src={`${myContextState.assetPrefix}/fixture/simulcastDay.svg`} /> {isMobile ? '' : fixtureConfig?.raceSeries2?.value?.toString()}</Box>
                                </StyledTableCell>
                                <StyledTableCell ismobile={isMobile.toString()} className={(isMobile ? "tabIconMobileSty " : '') + "tabIconSty"} style={{ width: isMobile ? "7.6vw" : "130px", color: "#173E96" }}>
                                    <Box><img src={`${myContextState.assetPrefix}/fixture/simulcastRace.svg`} />{isMobile ? '' : fixtureConfig?.raceSeries3?.value?.toString()}</Box>
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tabData.map((row: IFixture, index) => {
                                return (
                                    <StyledTableRow key={`${index}`}>
                                        <StyledTableCell ismobile={isMobile.toString()}>
                                            {getDisplayDate(row?.date_HK?.value?.toString())}
                                        </StyledTableCell>
                                        <StyledTableCell ismobile={isMobile.toString()} className="raceName">
                                            {getRaceNameCell(row)}
                                        </StyledTableCell>
                                        <StyledTableCell ismobile={isMobile.toString()} align="left" sx={{ width: isMobile ? "12vw" : "inherit" }}> {isMobile ? row.locationCode?.value?.toString() : row.locationName?.value?.toString()}</StyledTableCell>
                                        <StyledTableCell className={isMobile ? "tabCircleSty" : ""} ismobile={isMobile.toString()}><i className={getRaceSeriesValue(0, row) ? "isDisplay" : ""}></i> </StyledTableCell>
                                        <StyledTableCell className={isMobile ? "tabCircleSty" : ""} ismobile={isMobile.toString()}><i className={getRaceSeriesValue(1, row) ? "isDisplay" : ""}></i></StyledTableCell>
                                        <StyledTableCell className={isMobile ? "tabCircleSty" : ""} ismobile={isMobile.toString()}><i className={getRaceSeriesValue(2, row) ? "isDisplay" : ""}></i></StyledTableCell>
                                    </StyledTableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                    : <MessageDiv>{t("fixture_list_no_result")}</MessageDiv>}
            </TableContainer>

            {isMobile && (
                <IconNote className="iconNote">
                    <p className="tabIconMobileSty"><img src={`${myContextState.assetPrefix}/fixture/worldPool.svg`} />{fixtureConfig?.raceSeries1?.value?.toString()}</p>
                    <p className="tabIconMobileSty"><img src={`${myContextState.assetPrefix}/fixture/simulcastDay.svg`} />{fixtureConfig?.raceSeries2?.value?.toString()}</p>
                    <p className="tabIconMobileSty"><img src={`${myContextState.assetPrefix}/fixture/simulcastRace.svg`} />{fixtureConfig?.raceSeries3?.value?.toString()}</p>
                </IconNote>
            )}
        </Container>
    );
};
export default FixtureList;